import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import { toast } from "react-toastify";

import DistributionNetworkApi from "../../../../../infrastructure/api/distributionNetworkApi";
import DeleteConfirm from "../../../../components/shared/Dialog/DeleteConfirm";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";

const DistributionCompaniesPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, data: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DistributionNetworkApi.getCompanies(true);
      if (!res.ok) {
        toast.error(res.message || "خطا در دریافت لیست شرکت‌های توزیع");
        setRows([]);
        return;
      }
      setRows(
        (res.data || []).map((r) => ({
          ...r,
          portalReviewTitle:
            (r.portalReviewEnabled ?? r.PortalReviewEnabled) ? "فعال" : "—",
        }))
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = !term
    ? rows
    : rows.filter((r) =>
        `${r.name || ""} ${r.shortName || ""} ${r.province || ""}`
          .toLowerCase()
          .includes(term.toLowerCase())
      );

  const headCells = [
    { id: "updateOn", label: "بروزرسانی", type: "date" },
    { id: "name", label: "نام شرکت" },
    { id: "shortName", label: "نام کوتاه" },
    { id: "province", label: "استان" },
    { id: "areaCount", label: "تعداد امور" },
    { id: "contactCount", label: "تعداد مخاطب" },
    { id: "statusTitle", label: "وضعیت" },
    { id: "portalReviewTitle", label: "کارتابل توزیع (توافق)" },
  ];

  const confirmedDelete = async () => {
    const res = await DistributionNetworkApi.deleteCompany(deleteConfirm.data.id);
    return {
      isSuccess: res.ok,
      message: res.message || (res.ok ? "حذف شد" : "خطا در حذف"),
    };
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
        <DeleteConfirm
          message={`برای حذف "${deleteConfirm.data.name || ""}" اطمینان دارید؟`}
          open={deleteConfirm.open}
          onClose={(loadAgain) => {
            setDeleteConfirm({ open: false, data: {} });
            if (loadAgain) load();
          }}
          onSubmit={confirmedDelete}
        />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="شرکت‌های توزیع برق"
            action={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="outlined"
                  startIcon={<AccountTreeIcon />}
                  component={Link}
                  to="/SLN/Management/DistributionNetwork"
                  sx={{ mr: 1 }}
                >
                  کارتابل سلسله‌مراتبی
                </Button>
                <Link to="/SLN/Management/DistributionCompanies/create">
                  <Button variant="contained" startIcon={<AddIcon />}>
                    ایجاد
                  </Button>
                </Link>
                <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 1 }} />
                <IconButton color="primary" onClick={load} disabled={loading}>
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />
          <CardContent>
            <Box display="flex" gap={2} mb={2}>
              <TextField
                size="small"
                label="جستجو"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                sx={{ minWidth: 220 }}
              />
            </Box>
            <EnhancedTable
              rowKey="id"
              rows={filtered}
              headCells={headCells}
              pageSize={100}
              loading={loading}
              actions={[
                {
                  icon: <AccountTreeIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "مشاهده سلسله‌مراتبی",
                  onClick: (row) =>
                    navigate(
                      `/SLN/Management/DistributionNetwork?companyId=${row.id}`
                    ),
                },
                {
                  icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "ویرایش",
                  onClick: (row) =>
                    navigate(`/SLN/Management/DistributionCompanies/${row.id}`),
                },
                {
                  icon: <DeleteIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "حذف",
                  onClick: (row) => setDeleteConfirm({ open: true, data: row }),
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DistributionCompaniesPage;
